<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <div class="data-dump-report d-flex align-items-center p-2">
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDateRangeChange"
            ></date-range-picker>
            <div class="d-lg-block d-none filter-properties mr-1">
                <form-search-multiselect
                    v-model="filterProperties"
                    :options="filterPropertyOptions"
                    :icon="['fas', 'hotel']"
                    :responsiveness="false"
                    variant="primary"
                    placeholder="Filter Properties"
                />
            </div>
            <div class="d-lg-block d-none">
                <responsive-dropdown
                    :icon="['fas', 'filter']"
                    :items="filterByOptions"
                    :responsiveness="false"
                    variant="primary"
                >
                    {{ filterByLabel }}
                </responsive-dropdown>
            </div>
            <div class="d-lg-block d-none">
                <form-square-switch
                    v-model="includeReferrerData"
                    :labels="{
                        checked: 'Include Referrer Data',
                        unchecked: 'Include Referrer Data',
                    }"
                    class="include-referrer-data-toggle mr-1"
                    :sync="true"
                />
            </div>
            <div class="d-lg-block d-none">
                <form-square-switch
                    v-model="includeHostFee"
                    :labels="{
                        checked: 'Show Airbnb Host Fees',
                        unchecked: 'Show Airbnb Host Fees',
                    }"
                    class="include-airbnb-data-toggle mr-1"
                    :sync="true"
                />
            </div>
            <export-to-excel-button :href="exportExcelHref" />
            <report-settings-modal :modalId="reportSettingsModalId" :initialValues="reportSettings" :excelOnly="true" />
            <b-button-group class="d-block d-lg-none filters-dropdown ml-1">
                <more-filters-dropdown>
                    <template v-slot:default="{ handleDropdownShow, handleDropdownHide }">
                        <form-search-multiselect
                            v-model="filterProperties"
                            :options="filterPropertyOptions"
                            :icon="['fas', 'hotel']"
                            :responsiveness="false"
                            variant="primary"
                            placeholder="Filter Properties"
                            class="properties-dropdown-mobile"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        />
                        <responsive-dropdown
                            :icon="['fas', 'filter']"
                            :items="filterByOptions"
                            variant="primary"
                            :responsiveness="false"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        >
                            {{ filterByLabel }}
                        </responsive-dropdown>
                        <form-square-switch
                            v-model="includeReferrerData"
                            :labels="{
                                checked: 'Include Referrer Data',
                                unchecked: 'Include Referrer Data',
                            }"
                            class="include-referrer-data-toggle-mobile mb-1"
                            :sync="true"
                        />
                        <form-square-switch
                            v-model="includeHostFee"
                            :labels="{
                                checked: 'Show Airbnb Host Fees',
                                unchecked: 'Show Airbnb Host Fees',
                            }"
                            class="include-airbnb-data-toggle-mobile"
                            :sync="true"
                        />
                    </template>
                </more-filters-dropdown>
            </b-button-group>
        </div>
        <div class="p-2">
            {{ notification }}
        </div>
        <div class="p-2" v-if="includeHostFee">Airbnb host fees will be broken out from Total Revenues</div>
    </page>
</template>

<script>
import format from 'date-fns/format';
import API from '../../api';
import Page from '../generic/page/page.vue';
import { mapGetters } from 'vuex';
import DateRangePicker, {
    LAST_MONTH,
    LAST_WEEK,
    LAST_YEAR,
    nameToRange,
    THIS_MONTH,
    THIS_WEEK,
    THIS_YEAR,
    TODAY,
    YESTERDAY,
    CUSTOM_RANGE,
} from '../generic/date-range-picker/date-range-picker.vue';
import FormSearchMultiselect from '../generic/form-search-multiselect/form-search-multiselect.vue';
import ResponsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
import MoreFiltersDropdown from '../generic/more-filters-dropdown/more-filters-dropdown.vue';
import FormSquareSwitch from '../generic/form-square-switch/form-square-switch.vue';
import ExportToExcelButton from '../generic/export-to-excel-button/export-to-excel-button.vue';
import { isMediumScreen } from '../../utils/screens';
import { REPORT_DATA_DUMP_EXCEL_ENDPOINT, REPORT_DATA_DUMP_PROPERTIES_ENDPOINT } from '../../endpoints';
import ReportSettingsModal, {
    SELECT_INPUT,
    DATE_PERIODS_INPUT,
    SWITCH_INPUT,
    MULTI_SELECT_INPUT,
} from './report-settings/report-settings-modal.vue';

export default {
    name: 'DataDumpReport',
    components: {
        Page,
        DateRangePicker,
        FormSquareSwitch,
        FormSearchMultiselect,
        ResponsiveDropdown,
        MoreFiltersDropdown,
        ExportToExcelButton,
        ReportSettingsModal,
    },
    data() {
        return {
            busy: false,
            title: 'Data Dump Report',
            tabs: [
                {
                    title: 'Data Dump Report',
                    active: true,
                },
            ],
            dateRange: nameToRange(THIS_YEAR),
            dateRequestFormat: 'yyyy-MM-dd',
            predefinedRanges: [
                TODAY,
                YESTERDAY,
                THIS_WEEK,
                LAST_WEEK,
                THIS_MONTH,
                LAST_MONTH,
                THIS_YEAR,
                LAST_YEAR,
                CUSTOM_RANGE,
            ],
            filterProperties: [],
            filterPropertyOptions: [],
            filterBy: 'DEPARTURE_DATE',
            filterByLabel: 'Filter By Departure Date',
            includeReferrerData: false,
            notifications: {
                DEPARTURE_DATE: 'Invoice end date must be within date range to appear on report.',
                ARRIVAL_DATE: 'Invoice start date must be within date range to appear on report.',
                CREATED_DATE: 'Invoice entered date must be within date range to appear on report.',
            },
            reportSettingsModalId: 'report-settings-modal-id',
            includeHostFee: false,
        };
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat', 'userCurrencySymbol']),
        exportExcelHref() {
            let url = REPORT_DATA_DUMP_EXCEL_ENDPOINT();
            url += `?from_date=${this.startDateRequest}`;
            url += `&to_date=${this.endDateRequest}`;
            url += `&properties=${this.filterProperties.join(',')}`;
            url += `&filter_by=${this.filterBy}`;
            url += `&include_referrer_data=${this.includeReferrerData}`;
            url += `&include_host_fee=${this.includeHostFee}`;
            return url;
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        isMobile() {
            return !this.isMediumScreen();
        },
        filterByOptions() {
            return [
                {
                    label: 'Filter By Departure Date',
                    click: () => {
                        this.filterByLabel = 'Filter By Departure Date';
                        this.filterBy = 'DEPARTURE_DATE';
                    },
                },
                {
                    label: 'Filter By Arrival Date',
                    click: () => {
                        this.filterByLabel = 'Filter By Arrival Date';
                        this.filterBy = 'ARRIVAL_DATE';
                    },
                },
                {
                    label: 'Filter By Entered Date',
                    click: () => {
                        this.filterByLabel = 'Filter By Entered Date';
                        this.filterBy = 'CREATED_DATE';
                    },
                },
            ];
        },
        notification() {
            return this.notifications[this.filterBy];
        },
        reportSettings() {
            return {
                name: 'Data Dump',
                report_sysname: 'DATA_DUMP',
                settings: [
                    {
                        type: DATE_PERIODS_INPUT,
                        value: 'this_month',
                        label: 'Date Range',
                        name: 'date_range',
                        predefinedRanges: [
                            TODAY,
                            YESTERDAY,
                            THIS_WEEK,
                            LAST_WEEK,
                            THIS_MONTH,
                            LAST_MONTH,
                            THIS_YEAR,
                            LAST_YEAR,
                        ],
                    },
                    {
                        type: MULTI_SELECT_INPUT,
                        value: [],
                        name: 'properties',
                        label: 'Only for Properties',
                        options: this.filterPropertyOptions,
                        placeholder: 'All Properties',
                    },
                    {
                        type: SELECT_INPUT,
                        value: 'ARRIVAL_DATE',
                        name: 'filter_by',
                        options: [
                            {
                                text: 'Filter By Arrival Date',
                                value: 'ARRIVAL_DATE',
                            },
                            {
                                text: 'Filter By Entered Date',
                                value: 'CREATED_DATE',
                            },
                            {
                                text: 'Filter By Departure Date',
                                value: 'DEPARTURE_DATE',
                            },
                        ],
                        label: 'Filter By',
                    },
                    {
                        type: SWITCH_INPUT,
                        value: false,
                        name: 'include_referrer_data',
                        label: 'Include Referrer Data',
                    },
                    {
                        type: SWITCH_INPUT,
                        value: false,
                        name: 'include_host_fee',
                        label: 'Show Airbnb Host Fees',
                    },
                ],
            };
        },
    },
    mounted() {
        this.fetchProperties();
    },
    methods: {
        isMediumScreen,
        async fetchProperties() {
            try {
                const { data } = await API.get(REPORT_DATA_DUMP_PROPERTIES_ENDPOINT());
                this.filterPropertyOptions = data.map((item) => ({
                    value: item.property_id,
                    text: item.name,
                    disabled: item.disabled,
                }));
            } catch (e) {
                this.errorToast({ text: 'Could not fetch properties' });
            }
        },
        handleDateRangeChange(value) {
            this.dateRange = { ...value };
        },
    },
};
</script>

<style lang="scss">
.data-dump-report {
    .filter-properties {
        > fieldset {
            margin: 0 !important;
        }
    }
    .include-referrer-data-toggle,
    .include-airbnb-data-toggle {
        width: 180px;
        .v-switch-label {
            padding-top: 0;
            line-height: 34px !important;
        }
        .v-switch-core {
            height: 34px;
            .v-switch-button {
                height: 28px;
                top: 3px;
            }
        }
        &-mobile {
            width: 100%;
            .v-switch-label {
                padding-top: 0;
                line-height: 38px !important;
            }
            .v-switch-core {
                height: 38px;
                .v-switch-button {
                    height: 32px;
                    top: 3px;
                }
            }
        }
    }
    .include-airbnb-data-toggle {
        width: 190px !important;
    }
    .properties-dropdown-mobile {
        width: 100%;
    }
    .filters-dropdown {
        > div {
            height: 38px !important;
        }
    }
}
</style>